import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table, Row, Col, Form, InputGroup, Button,
  Pagination, Dropdown, Spinner, Modal
} from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchTransactionsList } from "../services/api";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default function PaymentHistory() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('date_creation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedPaiements, setSelectedPaiements] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  // ============================================
  // HELPERS
  // ============================================
  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' XAF';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'SUCCESS': 'Paiement réussi',
      'FAILED': 'Échec du paiement',
      'PENDING': 'En attente'
    };
    return statusMap[status] || status;
  };

  const getStatusConfig = (status) => {
    const config = {
      'SUCCESS': { color: '#10b981', bg: '#d1fae5', icon: 'bi-check-circle-fill', label: 'Succès' },
      'FAILED': { color: '#ef4444', bg: '#fee2e2', icon: 'bi-x-circle-fill', label: 'Échec' },
      'PENDING': { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-clock-fill', label: 'En attente' }
    };
    return config[status] || { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-question-circle', label: status };
  };

  // ============================================
  // CHARGEMENT DES TRANSACTIONS
  // ============================================
  const loadTransactions = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoadingTransactions(true);
      setError("");

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Token d'authentification non trouvé");
        return;
      }

      const transactionsData = await fetchTransactionsList(token);

      if (!Array.isArray(transactionsData)) {
        setError("Format de données invalide reçu du serveur");
        return;
      }

      const formattedTransactions = transactionsData.map(transaction => ({
        transaction_id: transaction.transaction_id,
        payment_method: transaction.payment_method,
        status: transaction.status,
        note: transaction.note || 'Aucune note',
        amount: parseFloat(transaction.amount) || 0,
        client_msisdn: transaction.client_msisdn,
        date_creation: transaction.created_at || new Date().toISOString()
      }));

      setTransactions(formattedTransactions);
    } catch (error) {
      console.error('❌ Erreur transactions:', error);
      setError("Erreur lors du chargement des transactions: " + error.message);
    } finally {
      setLoadingTransactions(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // CHARGEMENT DES DÉTAILS
  // ============================================
  const loadTransactionDetails = async (transactionId) => {
    try {
      setLoadingDetails(true);
      const response = await apiClient.get(`/check-payment-status/${transactionId}`);
      setTransactionDetails(response.data);
    } catch (error) {
      console.error('❌ Erreur détails:', error);
      if (error.response) {
        setError(`Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError("Aucune réponse du serveur. Vérifiez votre connexion.");
      } else {
        setError("Erreur lors du chargement des détails: " + error.message);
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // ============================================
  // RECU PDF
  // ============================================
  const downloadReceipt = () => {
    if (!transactionDetails) return;
    setDownloadingReceipt(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = 15;

      const formatAmountForPDF = (amount) => {
        if (!amount && amount !== 0) return 'Montant indisponible';
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        if (isNaN(numericAmount)) return 'Montant invalide';
        return new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numericAmount) + ' XAF';
      };

      const ensureString = (value) => {
        if (value === null || value === undefined) return '';
        return String(value);
      };

      const primaryColor = [41, 128, 185];
      const statusColors = {
        'SUCCESS': [39, 174, 96],
        'FAILED': [231, 76, 60],
        'PENDING': [243, 156, 18]
      };
      const lightColor = [248, 249, 250];

      const status = transactionDetails.payment?.status || 'PENDING';
      const statusColor = statusColors[status] || [150, 150, 150];

      const statusTexts = {
        'SUCCESS': 'PAIEMENT RÉUSSI',
        'FAILED': 'PAIEMENT ÉCHOUÉ',
        'PENDING': 'PAIEMENT EN ATTENTE'
      };
      const statusText = statusTexts[status] || 'STATUT INCONNU';

      // Header
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 40, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text(ensureString("REÇU DE PAIEMENT"), pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const subtitles = {
        'SUCCESS': 'Transaction sécurisée • Reçu officiel',
        'FAILED': 'Transaction échouée • Reçu informatif',
        'PENDING': 'Transaction en cours • Reçu provisoire'
      };
      doc.text(ensureString(subtitles[status] || 'Reçu de transaction'), pageWidth / 2, 28, { align: 'center' });

      doc.setFontSize(8);
      doc.text(ensureString(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`), margin, 35);
      doc.text(ensureString(`Ref: ${transactionDetails.txn_id}`), pageWidth - margin, 35, { align: 'right' });

      yPosition = 50;

      // Status
      doc.setFillColor(...statusColor);
      doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 12, 3, 3, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(ensureString(statusText), pageWidth / 2, yPosition + 8, { align: 'center' });

      yPosition += 20;

      // Montant
      const amount = transactionDetails.payment?.amount;
      const formattedAmount = formatAmountForPDF(amount);

      if (status === 'FAILED') doc.setTextColor(150, 150, 150);
      else if (status === 'PENDING') doc.setTextColor(243, 156, 18);
      else doc.setTextColor(0, 0, 0);

      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text(ensureString(formattedAmount), pageWidth / 2, yPosition, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      yPosition += 15;

      // Info
      doc.setFillColor(...lightColor);
      const infoHeight = 60;
      doc.rect(margin, yPosition, pageWidth - 2 * margin, infoHeight, 'F');

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      let infoY = yPosition + 8;
      const valueStart = margin + 55;

      const rows = [
        ["ID Transaction:", transactionDetails.txn_id || 'N/A'],
        ["Méthode:", transactionDetails.payment?.payment_method || 'En traitement'],
        ["Téléphone:", transactionDetails.payment?.client_msisdn || 'N/A'],
        ["Date:", transactionDetails.payment?.date_trans ? formatDate(transactionDetails.payment.date_trans) : `Début: ${new Date().toLocaleDateString('fr-FR')}`],
        ["Business ID:", transactionDetails.business_id || 'N/A'],
        ["Statut:", status === 'SUCCESS' ? 'Paiement confirmé et validé' : status === 'FAILED' ? 'Échec du traitement' : 'En attente de confirmation']
      ];

      rows.forEach(([label, value]) => {
        doc.setFont("helvetica", "bold");
        doc.text(ensureString(label), margin + 5, infoY);
        doc.setFont("helvetica", "normal");
        doc.text(ensureString(value), valueStart, infoY);
        infoY += 8;
      });

      yPosition += infoHeight + 5;

      // Note
      let noteContent = transactionDetails.payment?.note;
      if (!noteContent || noteContent === 'Aucune note') {
        const defaultNotes = {
          'SUCCESS': 'Paiement effectué avec succès. Merci pour votre confiance.',
          'FAILED': 'Le paiement n\'a pas pu être traité. Veuillez réessayer.',
          'PENDING': 'Votre transaction est en cours de traitement par le réseau mobile.'
        };
        noteContent = defaultNotes[status] || 'Aucune note disponible';
      }

      if (noteContent) {
        doc.setFillColor(...lightColor);
        const noteHeight = 25;
        doc.rect(margin, yPosition, pageWidth - 2 * margin, noteHeight, 'F');

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(ensureString("Information:"), margin + 5, yPosition + 8);

        doc.setFont("helvetica", "normal");
        const splitNote = doc.splitTextToSize(ensureString(noteContent), pageWidth - 2 * margin - 15);
        doc.text(splitNote, margin + 5, yPosition + 16);

        yPosition += noteHeight + 10;
      }

      // Résumé
      yPosition += 5;
      doc.setFillColor(...lightColor);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 20, 'F');

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(ensureString("Montant total:"), margin + 5, yPosition + 8);

      doc.setFontSize(12);
      doc.text(ensureString(formattedAmount), pageWidth - margin - 5, yPosition + 8, { align: 'right' });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(ensureString("Devise: XAF (Franc CFA)"), margin + 5, yPosition + 16);

      yPosition += 30;

      // Message final
      doc.setTextColor(100, 100, 100);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      const finalMessages = {
        'SUCCESS': 'Votre transaction a été traitée avec succès. Conservez ce reçu.',
        'FAILED': 'Veuillez contacter notre service client pour plus d\'informations.',
        'PENDING': 'Ce reçu est provisoire. Le statut définitif sera confirmé.'
      };
      const finalMessage = finalMessages[status] || 'Merci pour votre transaction.';
      const splitFinalMessage = doc.splitTextToSize(ensureString(finalMessage), pageWidth - 2 * margin - 10);
      doc.text(splitFinalMessage, pageWidth / 2, yPosition, { align: 'center' });

      // Footer
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(ensureString("Ce document est un reçu électronique."), pageWidth / 2, 282, { align: 'center' });

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      doc.save(`receipt-${transactionDetails.txn_id}-${timestamp}.pdf`);
    } catch (error) {
      console.error('❌ Erreur reçu:', error);
      setError("Erreur lors du téléchargement du reçu: " + error.message);
    } finally {
      setDownloadingReceipt(false);
    }
  };

  // ============================================
  // EXPORT
  // ============================================
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generatePDF = (data) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = 20;

      const pdfText = (text) => {
        if (text === null || text === undefined) return '';
        return String(text).replace(/&/g, 'et');
      };

      const primaryColor = [41, 128, 185];
      const lightColor = [248, 249, 250];
      const successColor = [39, 174, 96];
      const warningColor = [243, 156, 18];
      const dangerColor = [231, 76, 60];

      // Header
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 50, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("RAPPORT DES TRANSACTIONS", pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("Historique complet des paiements", pageWidth / 2, 28, { align: 'center' });

      doc.setFontSize(9);
      doc.text(`Genere le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 42);
      doc.text(`Total: ${data.length} transaction(s)`, pageWidth - margin, 42, { align: 'right' });

      yPosition = 60;

      // Stats
      const stats = {
        total: data.length,
        success: data.filter(t => t.status === 'SUCCESS').length,
        failed: data.filter(t => t.status === 'FAILED').length,
        pending: data.filter(t => t.status === 'PENDING').length,
        totalAmount: data.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
      };

      doc.setFillColor(...lightColor);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 25, 'F');

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("RESUME STATISTIQUE", margin + 5, yPosition + 8);

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Total: ${stats.total}`, margin + 5, yPosition + 16);
      doc.text(`Montant: ${pdfText(stats.totalAmount)} XAF`, pageWidth - margin - 5, yPosition + 16, { align: 'right' });
      doc.text(`Succes: ${stats.success} | Echecs: ${stats.failed} | En attente: ${stats.pending}`, margin + 5, yPosition + 22);

      yPosition += 35;

      // Table
      const headers = ['ID TRANSACTION', 'NUMERO', 'METHODE', 'STATUT', 'MONTANT', 'DATE'];
      const columnWidths = [32, 28, 25, 30, 32, 25];
      const rowHeight = 12;

      doc.setFillColor(60, 60, 60);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, rowHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");

      let xPosition = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPosition + 2, yPosition + 8);
        xPosition += columnWidths[index];
      });

      yPosition += rowHeight;

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      data.forEach((transaction, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
          doc.setFillColor(60, 60, 60);
          doc.rect(margin, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          xPosition = margin;
          headers.forEach((header, idx) => {
            doc.text(header, xPosition + 2, yPosition + 8);
            xPosition += columnWidths[idx];
          });
          yPosition += rowHeight;
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
        }

        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
        }

        xPosition = margin;

        const transactionId = pdfText(transaction.transaction_id?.substring(0, 20) + (transaction.transaction_id?.length > 20 ? '...' : '') || 'N/A');
        doc.text(transactionId, xPosition + 2, yPosition + 8);
        xPosition += columnWidths[0];

        doc.text(pdfText(transaction.client_msisdn || 'N/A'), xPosition + 2, yPosition + 8);
        xPosition += columnWidths[1];

        doc.text(pdfText(transaction.payment_method || 'N/A'), xPosition + 2, yPosition + 8);
        xPosition += columnWidths[2];

        const status = transaction.status;
        if (status === 'SUCCESS') doc.setTextColor(...successColor);
        else if (status === 'FAILED') doc.setTextColor(...dangerColor);
        else if (status === 'PENDING') doc.setTextColor(...warningColor);
        else doc.setTextColor(100, 100, 100);

        doc.text(pdfText(getStatusText(status)), xPosition + 2, yPosition + 8);
        xPosition += columnWidths[3];

        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold");
        doc.text(`${pdfText(transaction.amount)} XAF`, xPosition + 2, yPosition + 8);
        doc.setFont("helvetica", "normal");
        xPosition += columnWidths[4];

        doc.setTextColor(100, 100, 100);
        const shortDate = transaction.date_creation
          ? new Date(transaction.date_creation).toLocaleDateString('fr-FR')
          : 'N/A';
        doc.text(pdfText(shortDate), xPosition + 2, yPosition + 8);

        doc.setDrawColor(220, 220, 220);
        doc.line(margin, yPosition + rowHeight, pageWidth - margin, yPosition + rowHeight);

        yPosition += rowHeight;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(...primaryColor);
        doc.rect(0, 280, pageWidth, 20, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text("Rapport genere automatiquement", pageWidth / 2, 288, { align: 'center' });
        doc.text(`Page ${i} sur ${pageCount}`, pageWidth / 2, 293, { align: 'center' });
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      doc.save(`rapport-transactions-${timestamp}.pdf`);
    } catch (error) {
      console.error('❌ Erreur PDF:', error);
      throw new Error("Erreur PDF: " + error.message);
    }
  };

  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedPaiements.length > 0
      ? transactions.filter(p => selectedPaiements.includes(p.transaction_id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      alert("Aucune donnée à exporter");
      setExportLoading(false);
      return;
    }

    try {
      if (format === "csv") {
        const headers = ["ID", "Numéro", "Méthode", "Statut", "Montant", "Note", "Date"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...dataToExport.map(p => [
            p.transaction_id, p.client_msisdn, p.payment_method,
            getStatusText(p.status), p.amount, p.note, formatDate(p.date_creation)
          ].map(v => `"${v || ''}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, `export-transactions-${Date.now()}.csv`, "text/csv;charset=utf-8;");
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(dataToExport.map(p => ({
          "ID": p.transaction_id,
          "Numéro": p.client_msisdn,
          "Méthode": p.payment_method,
          "Statut": getStatusText(p.status),
          "Montant": p.amount,
          "Note": p.note,
          "Date": formatDate(p.date_creation)
        })));
        ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 30 }, { wch: 20 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transactions");
        XLSX.writeFile(wb, `export-transactions-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(dataToExport, null, 2), `export-transactions-${Date.now()}.json`, "application/json;charset=utf-8;");
      } else if (format === "pdf") {
        if (dataToExport.length === 1) {
          setTransactionDetails({
            payment: dataToExport[0],
            txn_id: dataToExport[0].transaction_id,
            business_id: dataToExport[0].business_id,
            code: dataToExport[0].code
          });
          setTimeout(() => downloadReceipt(), 100);
        } else {
          generatePDF(dataToExport);
        }
      }
    } catch (error) {
      console.error(`Erreur export: ${error.message}`);
      alert(`Erreur: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // ============================================
  // EFFETS
  // ============================================
  useEffect(() => {
    loadTransactions();
  }, []);

  const handleRefresh = () => {
    loadTransactions(true);
    setSelectedPaiements([]);
    setCurrentPage(1);
  };

  const handleShowDetails = async (transaction) => {
    setShowDetails(true);
    await loadTransactionDetails(transaction.transaction_id);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setTransactionDetails(null);
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    if (!transactions.length) return [];

    let filtered = transactions.filter(item =>
      item.transaction_id?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.payment_method?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.status?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.note?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.client_msisdn?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.amount?.toString().includes(filterText)
    );

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [transactions, filterText, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = transactions.length;
    const success = transactions.filter(t => t.status === 'SUCCESS').length;
    const failed = transactions.filter(t => t.status === 'FAILED').length;
    const pending = transactions.filter(t => t.status === 'PENDING').length;
    const totalAmount = transactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    const successRate = total > 0 ? Math.round((success / total) * 100) : 0;

    return { total, success, failed, pending, totalAmount, successRate };
  }, [transactions]);

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="ph-sortable-header" onClick={() => handleSort(field)}>
      <span className="ph-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ph-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="ph-page main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="ph-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="ph-header-icon">
              <i className="bi bi-receipt-cutoff"></i>
            </div>
            <div>
              <h3 className="ph-header-title mb-1">Historique des paiements</h3>
              <p className="ph-header-subtitle mb-0">
                Consultation et gestion de vos transactions
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="ph-btn-secondary" onClick={handleRefresh} disabled={refreshing || loadingTransactions}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat ph-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>

            <div className="dropdown">
              <button
                className="ph-btn-secondary"
                type="button"
                data-bs-toggle="dropdown"
                disabled={exportLoading || transactions.length === 0}
              >
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat ph-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </button>
              <ul className="dropdown-menu ph-dropdown-menu">
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('csv')}>
                    <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('excel')}>
                    <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('pdf')}>
                    <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('json')}>
                    <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                  </button>
                </li>
              </ul>
            </div>

            <Link to="/apps/Recharge" className="ph-btn-primary">
              <i className="bi bi-plus-circle"></i>
              <span>Nouveau paiement</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="ph-alert ph-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="ph-btn-retry" onClick={handleRefresh}>
            <i className="bi bi-arrow-clockwise"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-blue">
            <div className="ph-stat-icon-wrapper ph-stat-icon-blue">
              <i className="bi bi-list-ul"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Total transactions</div>
              <div className="ph-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-green">
            <div className="ph-stat-icon-wrapper ph-stat-icon-green">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Succès</div>
              <div className="ph-stat-value">{stats.success.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-amber">
            <div className="ph-stat-icon-wrapper ph-stat-icon-amber">
              <i className="bi bi-clock-fill"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">En attente</div>
              <div className="ph-stat-value">{stats.pending.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-violet">
            <div className="ph-stat-icon-wrapper ph-stat-icon-violet">
              <i className="bi bi-cash-stack"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Montant total</div>
              <div className="ph-stat-value">{new Intl.NumberFormat('fr-FR').format(Math.round(stats.totalAmount))}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="ph-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="ph-search-wrapper">
              <i className="bi bi-search ph-search-icon"></i>
              <input
                type="text"
                className="ph-search-input"
                placeholder="Rechercher une transaction..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="ph-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="ph-select-full"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6} className="text-md-end">
            <span className="ph-filter-info">
              <i className="bi bi-info-circle"></i>
              <strong>{filteredAndSortedItems.length}</strong> transaction{filteredAndSortedItems.length > 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="ph-table-card">
        {loadingTransactions ? (
          <div className="ph-loading">
            <div className="ph-loading-spinner"></div>
            <div className="ph-loading-text">Chargement des transactions...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="ph-table mb-0">
                <thead>
                  <tr>
                    <SortableHeader field="transaction_id">
                      <i className="bi bi-hash me-1"></i>ID Transaction
                    </SortableHeader>
                    <SortableHeader field="client_msisdn">
                      <i className="bi bi-telephone me-1"></i>Numéro
                    </SortableHeader>
                    <SortableHeader field="payment_method">
                      <i className="bi bi-credit-card me-1"></i>Méthode
                    </SortableHeader>
                    <SortableHeader field="status">
                      <i className="bi bi-flag me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="amount">
                      <i className="bi bi-cash-stack me-1"></i>Montant
                    </SortableHeader>
                    <SortableHeader field="date_creation">
                      <i className="bi bi-calendar me-1"></i>Date
                    </SortableHeader>
                    <th className="text-end" style={{ width: '120px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((paiement) => {
                      const statusConf = getStatusConfig(paiement.status);
                      return (
                        <tr key={paiement.transaction_id} className="ph-row">
                          <td>
                            <code className="ph-code">{paiement.transaction_id}</code>
                          </td>
                          <td>
                            <span className="ph-phone-badge">
                              <i className="bi bi-telephone-fill"></i>
                              {paiement.client_msisdn}
                            </span>
                          </td>
                          <td>
                            <span className="ph-method">{paiement.payment_method}</span>
                          </td>
                          <td>
                            <span
                              className="ph-status-badge"
                              style={{ color: statusConf.color, backgroundColor: statusConf.bg }}
                            >
                              <i className={`bi ${statusConf.icon}`}></i>
                              {statusConf.label}
                            </span>
                          </td>
                          <td>
                            <span className="ph-price">{formatAmount(paiement.amount)}</span>
                          </td>
                          <td>
                            <span className="ph-date-cell">{formatDate(paiement.date_creation)}</span>
                          </td>
                          <td className="text-end">
                            <div className="ph-actions">
                              <button
                                className="ph-action-btn ph-action-view"
                                onClick={() => handleShowDetails(paiement)}
                                title="Voir les détails"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              <button
                                className="ph-action-btn ph-action-receipt"
                                onClick={() => handleShowDetails(paiement)}
                                title="Télécharger le reçu"
                              >
                                <i className="bi bi-receipt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="ph-empty-state">
                          <div className="ph-empty-icon">
                            <i className="bi bi-credit-card"></i>
                          </div>
                          <h5 className="ph-empty-title">Aucune transaction trouvée</h5>
                          <p className="ph-empty-text">
                            {filterText
                              ? "Aucune transaction ne correspond à votre recherche"
                              : "Aucune transaction disponible pour le moment"}
                          </p>
                          {filterText ? (
                            <button className="ph-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          ) : (
                            <Link to="/apps/Recharge" className="ph-btn-empty-reset">
                              <i className="bi bi-plus-circle me-2"></i>
                              Effectuer un paiement
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="ph-pagination">
                <div className="ph-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                </div>
                <div className="ph-pagination-controls">
                  <button className="ph-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="ph-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`ph-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button className="ph-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="ph-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================
          MODAL DÉTAILS
          ============================================ */}
      <Modal show={showDetails} onHide={handleCloseDetails} size="lg" centered className="ph-modal">
        <div className="ph-modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="ph-modal-icon">
              <i className="bi bi-receipt"></i>
            </div>
            <div>
              <h5 className="ph-modal-title mb-0">Détails de la transaction</h5>
              <small className="ph-modal-subtitle">
                {transactionDetails?.txn_id || 'Chargement...'}
              </small>
            </div>
          </div>
          <button className="ph-modal-close" onClick={handleCloseDetails}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="ph-modal-body">
          {loadingDetails ? (
            <div className="ph-detail-loading">
              <div className="ph-loading-spinner"></div>
              <div className="ph-loading-text">Chargement des détails...</div>
            </div>
          ) : transactionDetails ? (
            <>
              {/* Hero */}
              <div className="ph-detail-hero">
                <div
                  className="ph-detail-hero-icon"
                  style={{
                    background: getStatusConfig(transactionDetails.payment?.status).bg,
                    color: getStatusConfig(transactionDetails.payment?.status).color
                  }}
                >
                  <i className={`bi ${getStatusConfig(transactionDetails.payment?.status).icon}`}></i>
                </div>
                <div className="ph-detail-hero-content">
                  <div className="ph-detail-hero-label">Montant</div>
                  <div className="ph-detail-hero-value">
                    {formatAmount(transactionDetails.payment?.amount)}
                  </div>
                </div>
                <div className="ph-detail-hero-status">
                  <span
                    className="ph-status-badge"
                    style={{
                      color: getStatusConfig(transactionDetails.payment?.status).color,
                      backgroundColor: getStatusConfig(transactionDetails.payment?.status).bg
                    }}
                  >
                    <i className={`bi ${getStatusConfig(transactionDetails.payment?.status).icon}`}></i>
                    {getStatusConfig(transactionDetails.payment?.status).label}
                  </span>
                </div>
              </div>

              {/* Détails */}
              <div className="ph-detail-section">
                <div className="ph-detail-section-title">
                  <i className="bi bi-info-circle"></i>
                  Informations
                </div>
                <div className="ph-detail-grid">
                  <div className="ph-detail-item">
                    <div className="ph-detail-label">
                      <i className="bi bi-hash"></i>ID Transaction
                    </div>
                    <div className="ph-detail-value ph-detail-value-mono">
                      {transactionDetails.txn_id || '-'}
                    </div>
                  </div>
                  <div className="ph-detail-item">
                    <div className="ph-detail-label">
                      <i className="bi bi-building"></i>Business ID
                    </div>
                    <div className="ph-detail-value ph-detail-value-mono">
                      {transactionDetails.business_id || '-'}
                    </div>
                  </div>
                  <div className="ph-detail-item">
                    <div className="ph-detail-label">
                      <i className="bi bi-credit-card"></i>Méthode
                    </div>
                    <div className="ph-detail-value">
                      {transactionDetails.payment?.payment_method || '-'}
                    </div>
                  </div>
                  <div className="ph-detail-item">
                    <div className="ph-detail-label">
                      <i className="bi bi-telephone"></i>Numéro client
                    </div>
                    <div className="ph-detail-value ph-detail-value-mono">
                      {transactionDetails.payment?.client_msisdn || '-'}
                    </div>
                  </div>
                  <div className="ph-detail-item ph-detail-item-full">
                    <div className="ph-detail-label">
                      <i className="bi bi-calendar"></i>Date de transaction
                    </div>
                    <div className="ph-detail-value">
                      {formatDate(transactionDetails.payment?.date_trans) || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="ph-detail-note">
                <div className="ph-detail-note-header">
                  <i className="bi bi-chat-text"></i>
                  Notes et commentaires
                </div>
                <div className="ph-detail-note-body">
                  {transactionDetails.payment?.note || 'Aucune note disponible'}
                </div>
              </div>
            </>
          ) : (
            <div className="ph-detail-empty">
              <i className="bi bi-exclamation-triangle"></i>
              <p>Impossible de charger les détails de la transaction</p>
            </div>
          )}
        </Modal.Body>

        <div className="ph-modal-footer">
          <button className="ph-btn-cancel" onClick={handleCloseDetails}>
            <i className="bi bi-x-lg"></i>
            Fermer
          </button>
          <button
            className="ph-btn-primary"
            onClick={downloadReceipt}
            disabled={downloadingReceipt || !transactionDetails}
          >
            {downloadingReceipt ? (
              <>
                <Spinner animation="border" size="sm" />
                Génération...
              </>
            ) : (
              <>
                <i className="bi bi-download"></i>
                Télécharger le reçu
              </>
            )}
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .ph-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .ph-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .ph-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ph-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .ph-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .ph-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .ph-btn-primary, .ph-btn-secondary, .ph-btn-cancel, .ph-btn-retry {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .ph-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .ph-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .ph-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .ph-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .ph-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .ph-btn-secondary:disabled {
          opacity: 0.6;
        }
        .ph-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .ph-btn-cancel:hover {
          background: #f1f5f9;
        }
        .ph-btn-retry {
          background: white;
          color: #991b1b;
          border: 1px solid #fecaca;
          padding: 6px 14px;
          font-size: 0.8rem;
        }
        .ph-btn-retry:hover {
          background: #991b1b;
          color: white;
          border-color: #991b1b;
        }
        .ph-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
          position: absolute;
          z-index: 1000;
        }
        .ph-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }
        .ph-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== ALERTS ========== */
        .ph-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .ph-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .ph-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .ph-alert-danger > i {
          color: #ef4444;
        }

        /* ========== STATS ========== */
        .ph-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .ph-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .ph-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .ph-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .ph-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .ph-stat-icon-amber  { background: #fef3c7; color: #f59e0b; }
        .ph-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .ph-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .ph-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .ph-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .ph-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .ph-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .ph-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .ph-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .ph-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .ph-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .ph-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .ph-select-full {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .ph-select-full:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .ph-filter-info {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 11px 16px;
          background: #f1f5f9;
          border-radius: 10px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .ph-filter-info strong {
          color: #3b82f6;
        }

        /* ========== TABLE ========== */
        .ph-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ph-table {
          font-size: 0.875rem;
        }
        .ph-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .ph-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .ph-row {
          transition: background-color 0.15s;
        }
        .ph-row:hover {
          background-color: #f8fafc;
        }
        .ph-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .ph-sortable-header:hover {
          color: #3b82f6;
        }
        .ph-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .ph-sort-icon {
          color: #3b82f6;
          font-size: 0.75rem;
        }

        .ph-code {
          font-size: 0.72rem;
          color: #1e40af;
          background: #eff6ff;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 700;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          display: inline-block;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ph-phone-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          background: #f1f5f9;
          color: #334155;
          border: 1px solid #e2e8f0;
        }
        .ph-phone-badge > i {
          color: #94a3b8;
          font-size: 0.7rem;
        }

        .ph-method {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 600;
        }

        .ph-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 11px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.72rem;
          white-space: nowrap;
        }

        .ph-price {
          display: inline-block;
          font-weight: 700;
          color: #059669;
          font-size: 0.9rem;
          font-variant-numeric: tabular-nums;
          padding: 4px 10px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-radius: 6px;
        }

        .ph-date-cell {
          color: #64748b;
          font-size: 0.78rem;
          font-variant-numeric: tabular-nums;
        }

        .ph-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .ph-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.9rem;
        }
        .ph-action-view:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }
        .ph-action-receipt:hover {
          background: #06b6d4;
          border-color: #06b6d4;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .ph-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .ph-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: ph-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .ph-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .ph-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .ph-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .ph-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .ph-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .ph-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        .ph-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .ph-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .ph-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ph-pagination-info strong {
          color: #0f172a;
        }
        .ph-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ph-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .ph-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .ph-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ph-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== MODAL ========== */
        .ph-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .ph-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        .ph-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .ph-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .ph-modal-subtitle {
          color: #94a3b8;
          font-size: 0.8rem;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }
        .ph-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ph-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .ph-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .ph-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Detail Hero */
        .ph-detail-hero {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .ph-detail-hero-icon {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
        }
        .ph-detail-hero-content {
          flex-grow: 1;
          min-width: 0;
        }
        .ph-detail-hero-label {
          font-size: 0.72rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .ph-detail-hero-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .ph-detail-section {
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          padding: 20px;
          margin-bottom: 16px;
        }
        .ph-detail-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .ph-detail-section-title > i {
          color: #3b82f6;
          font-size: 1rem;
        }
        .ph-detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .ph-detail-item {
          background: #f8fafc;
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid #f1f5f9;
        }
        .ph-detail-item-full {
          grid-column: 1 / -1;
        }
        .ph-detail-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.68rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 6px;
        }
        .ph-detail-label > i {
          color: #cbd5e1;
        }
        .ph-detail-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          word-break: break-word;
        }
        .ph-detail-value-mono {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.78rem;
          color: #1e40af;
        }

        .ph-detail-note {
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .ph-detail-note-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 18px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
        }
        .ph-detail-note-header > i {
          color: #3b82f6;
        }
        .ph-detail-note-body {
          padding: 16px 18px;
          color: #475569;
          font-size: 0.85rem;
          line-height: 1.6;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
        }

        .ph-detail-loading {
          padding: 60px 20px;
          text-align: center;
        }
        .ph-detail-empty {
          padding: 60px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .ph-detail-empty > i {
          font-size: 3rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 12px;
        }

        /* ========== ANIMATIONS ========== */
        .ph-spin {
          animation: ph-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes ph-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .ph-detail-grid {
            grid-template-columns: 1fr;
          }
          .ph-detail-item-full {
            grid-column: 1;
          }
        }
        @media (max-width: 768px) {
          .ph-header {
            padding: 20px;
          }
          .ph-header-title {
            font-size: 1.2rem;
          }
          .ph-pagination {
            flex-direction: column;
          }
          .ph-stat-value {
            font-size: 1.25rem;
          }
          .ph-modal-body {
            padding: 16px;
          }
          .ph-modal-footer {
            flex-direction: column;
          }
          .ph-modal-footer > * {
            width: 100%;
          }
          .ph-detail-hero {
            padding: 16px;
          }
          .ph-detail-hero-icon {
            width: 52px;
            height: 52px;
            font-size: 22px;
          }
        }
      `}</style>
    </div>
  );
}